import { NavLink, useNavigate } from "react-router-dom";
import { useSession } from "../auth/session";
import { categoryMeta, orderCategories } from "../registry/categories";
import { listCategories, listCores } from "../registry/toolRegistry";
import { useUiStore } from "../state/uiStore";
import { cx, Icon } from "../ui";

// §11 Linke Rail. Oben Brand, darunter App-Nav (Explore/Assets/Generate/Canvas), dann —
// registry-getrieben (§4) — die Tool-Kategorien als Schnellsprung in das Studio. Unten
// API/Account/Theme. „Nichts referenziert ein Tool hart": die Kategorien kommen aus
// listCategories(), nie aus einer fixen Liste.

function RailItem({
  to,
  icon,
  label,
  end,
}: {
  to: string;
  icon: string;
  label: string;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cx(
          "group flex w-full flex-col items-center gap-1 rounded-lg py-2 text-[10px] font-medium transition-colors",
          isActive ? "text-accent" : "text-muted hover:text-text",
        )
      }
    >
      {({ isActive }) => (
        <>
          <span className={cx("grid h-9 w-9 place-items-center rounded-lg", isActive && "bg-accent/15")}>
            <Icon name={icon} size={20} />
          </span>
          <span className="leading-none">{label}</span>
        </>
      )}
    </NavLink>
  );
}

export function SideRail() {
  const navigate = useNavigate();
  const { theme, toggleTheme, openAuth } = useUiStore();
  const { status, user } = useSession();
  const categories = orderCategories(listCategories());

  function openCategory(cat: string) {
    const first = listCores(cat)[0];
    navigate(first ? `/generate/${cat}/${first.id}` : "/generate");
  }

  return (
    <nav className="flex h-full w-[64px] shrink-0 flex-col items-center gap-1 overflow-y-auto border-r border-border bg-surface px-2 py-3 sm:w-[76px]">
      {/* Brand */}
      <NavLink to="/" className="mb-1 grid h-10 w-10 place-items-center rounded-xl2 text-accent" aria-label="Loumy">
        <Icon name="logo" size={26} strokeWidth={2} />
      </NavLink>

      {/* App-Nav */}
      <RailItem to="/explore" icon="explore" label="Explore" />
      <RailItem to="/assets" icon="assets" label="Assets" />
      <RailItem to="/generate" icon="generate" label="Generate" />
      <RailItem to="/studio" icon="scissors" label="Studio" />
      <RailItem to="/editor" icon="canvas" label="Editor" />

      <div className="my-1 h-px w-8 bg-border" />

      {/* Kategorien (registry-getrieben, §4) */}
      {categories.map((c) => {
        const meta = categoryMeta(c);
        return (
          <button
            key={c}
            type="button"
            onClick={() => openCategory(c)}
            className="flex w-full flex-col items-center gap-1 rounded-lg py-2 text-[10px] font-medium text-muted transition-colors hover:text-text"
            title={meta.label}
          >
            <span className="grid h-9 w-9 place-items-center rounded-lg">
              <Icon name={meta.icon} size={20} />
            </span>
            <span className="leading-none">{meta.label}</span>
          </button>
        );
      })}

      <div className="mt-auto flex shrink-0 flex-col items-center gap-1 pt-2">
        <RailItem to="/mcp" icon="api" label="API" />
        <button
          type="button"
          onClick={toggleTheme}
          className="grid h-9 w-9 place-items-center rounded-lg text-muted hover:text-text"
          title={theme === "dark" ? "Light-Theme" : "Dark-Theme"}
        >
          <Icon name={theme === "dark" ? "sun" : "moon"} size={18} />
        </button>
        {status === "signed-in" ? (
          <NavLink
            to="/account"
            className="grid h-9 w-9 place-items-center rounded-full border border-border bg-surface-2 text-muted hover:text-text"
            title={user?.email ?? "Account"}
          >
            <Icon name="user" size={18} />
          </NavLink>
        ) : status === "loading" ? (
          // Session wird noch ermittelt → neutrales, nicht-interaktives Icon (kein Anmelden-Flash).
          <span
            className="grid h-9 w-9 place-items-center rounded-full border border-border bg-surface-2 text-muted/40"
            aria-hidden="true"
          >
            <Icon name="user" size={18} />
          </span>
        ) : (
          <button
            type="button"
            onClick={openAuth}
            className="grid h-9 w-9 place-items-center rounded-full border border-border bg-surface-2 text-muted hover:text-text"
            title="Anmelden / Registrieren"
          >
            <Icon name="user" size={18} />
          </button>
        )}
      </div>
    </nav>
  );
}
